// @ts-nocheck
import { fetchParameterRecords, fetchParameterRecordsByTime } from "../api/awsLamba";
import { generateClient } from "aws-amplify/api";
import { downloadData } from "aws-amplify/storage";
import type { DataSourceFormState, ForecastSourceType, PreviewData } from "../types/composedForecast";
import { applyOperation, parseCsvRows } from "../dataScienceOperations";
import { CUSTOM_VIEW_SOURCE_COLORS, cssColorToHex, readSwatch } from "./colorUtils";
import { formatTimeAgo } from "./dateUtils";
import { fetchAndParseForecastRecord, parseForecastFileText } from "./modelOutputUtils";
import { recordsToPoints } from "./chartTimeAxis";
import type { SparklinePoint } from "./chartTimeAxis";
import type { SourceDisplaySettings } from "./composedForecastDisplay";
import { scoreForecast } from "./forecastAccuracy";
import type { AccuracyConfig, AccuracyResult, ForecastPoint, ObservationPoint } from "./forecastAccuracy";
import { orderedSources, parseMetadata, resolveSourceRoles, resolveFormSourceRoles, savedObservationFlag, viewHistoryDays } from "./composedForecastRoles";
import { DEFAULT_RANGE_LOOKBACK_DAYS, rangeSourceLookbackDays, rangeSourceRef } from "./seriesSources";
import type { SeriesLine, SeriesRef, SeriesWindow } from "./seriesSources";
import { fetchSeriesWindow } from "./seriesSourcesFetch";
import { normalizeWeight, weightPoints, weightRecords } from "./composedForecastWeight";
import { describeQueryAggregation } from "./datasetQueryApi";
import { pickModelKey } from "./seriesMetaData";

// The role rules live in composedForecastRoles.ts (pure); re-exported so existing imports keep working
export { isSourceConfigured, orderedSources, parseMetadata, resolveSourceRoles, resolveFormSourceRoles, sourceConfigurationError, viewHistoryDays } from "./composedForecastRoles";

const client = generateClient();

const DAY_MS = 24 * 60 * 60 * 1000;

// A percentile series key as the forecast JSON / CSV adapter writes it: "0.10" (fraction) or "p10".
const percentileOfKey = (key: string): number | null => {
  const pMatch = /^p(\d{1,3})$/i.exec(key);
  if (pMatch) return Number(pMatch[1]);
  const fraction = Number(key);
  return key.trim() !== "" && !Number.isNaN(fraction) && fraction >= 0 && fraction <= 1 ? fraction * 100 : null;
};

/**
 * The one value a processed-model forecast point contributes as a dashboard line: the mean, else the median,
 * else the midpoint of its outermost percentile band (never a band edge such as p5), else its first number.
 */
export const forecastPointCentreValue = (point: Record<string, unknown>): number | undefined => {
  for (const key of ["mean", "0.5", "0.50", "p50"]) {
    if (typeof point[key] === "number") return point[key] as number;
  }
  const bands = Object.entries(point)
    .map(([key, value]) => ({ pct: percentileOfKey(key), value }))
    .filter((b): b is { pct: number; value: number } => b.pct !== null && typeof b.value === "number")
    .sort((a, b) => a.pct - b.pct);
  if (bands.length) return (bands[0].value + bands[bands.length - 1].value) / 2;
  return Object.values(point).find((v): v is number => typeof v === "number");
};

// Pull the (single) operation out of a DataSource's dataOperations AWSJSON. Stored shape: { pipeline: [{ id, args, version }] }. Falls back to null when the field is empty or references an unknown op.
export function parseOperationFromDataOperations(raw: unknown): any | null {
  const parsed = parseMetadata(raw);
  const first = parsed?.pipeline?.[0];
  if (!first?.id) return null;
  return { id: first.id, args: first.args ?? {}, version: first.version ?? 1 };
}

// ============================================ Form State ============================================

/** A saved data source as the form edits it; a DATASET is stored as S3_DATA (no DATASET enum yet) and is unpacked from its metadata. */
export function dataSourceToFormState(ds: any): DataSourceFormState {
  const formState: DataSourceFormState = {
    id: ds.id,
    sourceType: ds.sourceType as ForecastSourceType,
    sourceName: ds.sourceName || "",
    sourceDescription: ds.sourceDescription || "",
    weight: ds.weight ?? 1.0,
    priority: ds.priority ?? 0,
  };

  if (ds.sourceType === "PROCESSED_MODEL_OUTPUT") {
    formState.processedModelOutputId = ds.processedModelOutputId;
    formState.processedModelOutputName = ds.processedModelOutput?.name;
    formState.schemaName = ds.processedModelOutput?.schema?.name;
    const variation = parseMetadata(ds.metadata)?.variation;
    if (variation) formState.variation = variation;
  }

  if (ds.sourceType === "POSTGRES_HISTORICAL" && ds.metadata) {
    const meta = parseMetadata(ds.metadata);
    formState.parameterId = meta.parameterId;
    formState.parameterName = meta.parameterName;
    formState.datasourceName = meta.datasourceName;
    formState.lookbackDays = meta.lookbackDays;
  }

  if (ds.sourceType === "S3_DATA" && ds.metadata) {
    const meta = parseMetadata(ds.metadata);
    if (meta.datasetId) {
      formState.sourceType = "DATASET";
      formState.datasetId = meta.datasetId;
      formState.datasetName = meta.datasetName;
      formState.datasetRecordId = meta.datasetRecordId;
      formState.datasetRecordS3Path = meta.datasetRecordS3Path;
      formState.datasetRecordFileName = meta.datasetRecordFileName;
      // A curated dataset is read by column over a range, not from a pinned file (stage 5, decision 4)
      if (meta.curated) {
        formState.curated = true;
        formState.column = meta.column;
        formState.lookbackDays = meta.lookbackDays;
      }
    }
  }

  if (ds.dataOperations) {
    formState.operation = parseOperationFromDataOperations(ds.dataOperations);
  }

  // Accuracy + display settings live on metadata for every source type; carry them so edit/diff round-trips them
  const accuracy = parseMetadata(ds.metadata)?.accuracy;
  if (accuracy?.enabled) formState.accuracy = accuracy;
  const display = parseMetadata(ds.metadata)?.display;
  if (display) formState.display = display;
  // List position (also the draw order) and the view's history setting, kept on the base source
  const meta = parseMetadata(ds.metadata);
  if (typeof meta.drawOrder === "number") formState.drawOrder = meta.drawOrder;
  if (typeof meta.historyDays === "number") formState.historyDays = meta.historyDays;
  if (meta.historyAnchor === "today") formState.historyAnchor = "today";

  return formState;
}

export interface ComposedForecastFormFields {
  name: string;
  description: string;
  forecastType: string;
  aggregationMethod: string;
  isActive: boolean;
  dataSources: DataSourceFormState[];
}

/** The form's starting values: a saved view's own, or the defaults for a new one. */
export const forecastToFormFields = (forecast?: any): ComposedForecastFormFields => ({
  name: forecast?.name || "",
  description: forecast?.description || "",
  forecastType: forecast?.forecastType || "ensemble",
  aggregationMethod: forecast?.aggregationMethod || "mean",
  isActive: forecast?.isActive ?? true,
  dataSources: orderedSources(forecast?.dataSources?.items).map(dataSourceToFormState),
});

/**
 * The form rows as they save: each takes its list position as drawOrder (the top row is drawn on top), and the
 * view's history setting rides on the main forecast (the base source) only. A row's form-only key is dropped.
 */
export function stampSourceOrder(
  rows: DataSourceFormState[],
  historyDays: number | null,
  historyAnchor: HistoryAnchor = "issue"
): DataSourceFormState[] {
  const { baseIndex } = resolveFormSourceRoles(rows);
  return rows.map(({ clientKey, historyDays: _days, historyAnchor: _anchor, ...row }, index) => ({
    ...row,
    drawOrder: index,
    ...(index === baseIndex && historyDays && historyDays > 0 ? { historyDays } : {}),
    // "issue" is the default, so only "today" is saved
    ...(index === baseIndex && historyAnchor === "today" ? { historyAnchor } : {}),
  }));
}

// Empty string, null and undefined all mean "not set", so a field the form touched but left blank isn't a change
const normalizeFormValue = (value: unknown): unknown =>
  value === undefined || value === null || value === "" ? null : typeof value === "object" ? JSON.stringify(value) : value;

/** True when two form sources would save the same thing; key order and blank-vs-missing don't count. */
export const sameFormSource = (a: DataSourceFormState, b: DataSourceFormState): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).every((key) => normalizeFormValue(a[key]) === normalizeFormValue(b[key]));
};

export interface DataSourceChanges {
  toRemove: string[];
  toUpdate: DataSourceFormState[];
  toCreate: DataSourceFormState[];
}

/** What a save has to write: saved sources no longer in the form are removed, only sources whose fields changed are updated, and sources without an id are created. */
export function diffDataSources(savedItems: any[], formSources: DataSourceFormState[]): DataSourceChanges {
  const saved = new Map((savedItems || []).map((ds) => [ds.id, dataSourceToFormState(ds)]));
  const keptIds = new Set(formSources.filter((ds) => ds.id).map((ds) => ds.id));
  return {
    toRemove: Array.from(saved.keys()).filter((id) => !keptIds.has(id)),
    toUpdate: formSources.filter((ds) => ds.id && (!saved.has(ds.id) || !sameFormSource(saved.get(ds.id), ds))),
    toCreate: formSources.filter((ds) => !ds.id),
  };
}

/** Fetch preview data for a given data source configuration. Dispatches to the correct fetch strategy based on sourceType. */
export async function fetchPreviewData(
  sourceType: ForecastSourceType,
  config: DataSourceFormState
): Promise<PreviewData> {
  switch (sourceType) {
    case "POSTGRES_HISTORICAL":
      return fetchPostgresPreview(config);
    case "PROCESSED_MODEL_OUTPUT":
      return fetchProcessedModelPreview(config);
    case "DATASET":
      return config.curated ? fetchCuratedDatasetPreview(config) : fetchDatasetPreview(config);
    default:
      throw new Error(`Preview not supported for source type: ${sourceType}`);
  }
}

// ---- Range sources (stage 5): a curated dataset (a client's upload, or one a historical model writes), read over a window ----

/** "daily averages" from the query's aggregation, for a legend or a preview warning; null when the rows came back as they are. */
export const shortAggregationNote = (aggregation: SeriesLine["aggregation"]): string | null => {
  if (!aggregation?.applied) return null;
  const words = describeQueryAggregation({ aggregation, columns: [] } as any);
  return words ? words.replace(/^Showing /, "").replace(/ of [\d,]+ rows.*$/, "") : null;
};

/** A range source's line over the server's default window (no base forecast is known at preview time), honouring its lookback. */
async function fetchRangePreview(ref: SeriesRef, label: string, lookbackDays?: number): Promise<PreviewData> {
  const line = await fetchSeriesWindow(ref, null, { lookbackDays: lookbackDays || DEFAULT_RANGE_LOOKBACK_DAYS });
  if (!line.timestamps.length) throw new Error("No data in this range");
  const note = shortAggregationNote(line.aggregation);
  return {
    timestamps: line.timestamps,
    values: line.values,
    label,
    unit: line.unit,
    warnings: note ? [`Showing ${note}`] : undefined,
  };
}

/** A curated DATASET: one column over a range through the dataset query, instead of one pinned upload file. */
async function fetchCuratedDatasetPreview(config: DataSourceFormState): Promise<PreviewData> {
  if (!config.datasetId) throw new Error("No dataset selected");
  if (!config.column) throw new Error("No column selected");
  return fetchRangePreview(
    { kind: "dataset", id: config.datasetId, column: config.column },
    config.sourceName || `${config.datasetName || "Dataset"} · ${config.column}`,
    config.lookbackDays
  );
}

/** POSTGRES_HISTORICAL: Fetch historical records via the datasource REST API */
async function fetchPostgresPreview(config: DataSourceFormState): Promise<PreviewData> {
  if (!config.parameterId) throw new Error("No parameter selected");

  const records = await fetchParameterRecords(
    config.parameterId,
    config.lookbackDays || 30
  );

  if (!records || records.length === 0) {
    throw new Error("No records found for this parameter");
  }

  // Sort by timestamp
  const sorted = [...records].sort(
    (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return {
    timestamps: sorted.map((r: any) => new Date(r.timestamp)),
    values: sorted.map((r: any) => parseFloat(r.value) || 0),
    label: config.parameterName || "Postgres Data",
    unit: undefined,
  };
}

/** PROCESSED_MODEL_OUTPUT: Fetch the latest forecast record from S3. Forecast files are JSON (not CSV) with percentile/channel data. We extract the "mean" channel (or first available) for the inline preview. */
async function fetchProcessedModelPreview(config: DataSourceFormState): Promise<PreviewData> {
  if (!config.processedModelOutputId) throw new Error("No output parameter selected");

  // Fetch the output param with its latest forecast
  const { data } = await client.graphql({
    query: /* GraphQL */ `
      query GetOutputWithForecasts($id: ID!) {
        getProcessedModelOutputParam(id: $id) {
          id
          name
          unit
          outputLocation
          forecasts(sortDirection: DESC, limit: 1) {
            items {
              id
              forecastIssued
              s3Key
            }
          }
        }
      }
    `,
    variables: { id: config.processedModelOutputId },
  });

  const output = data.getProcessedModelOutputParam;
  if (!output) throw new Error("Output parameter not found");

  const latestForecast = output.forecasts?.items?.[0];
  if (!latestForecast?.s3Key) {
    throw new Error("No forecast records available for this output parameter");
  }

  // Download and parse the forecast file (JSON or Standard time-series CSV) from S3
  try {
    const result = await downloadData({ path: latestForecast.s3Key }).result;
    const text = await result.body.text();
    const parsedData = parseForecastFileText(text, latestForecast.s3Key);

    // The source's chosen variable, else the first (same structure as the ModelOutput preview)
    const models = parsedData?.models || {};
    const modelKey = pickModelKey(models, config.variation);
    if (!modelKey) throw new Error("No model data found in forecast file");

    const modelData = models[modelKey]?.data;
    if (!modelData) throw new Error("No data found in forecast model");

    // Extract timestamps and the "mean" channel (fallback to "0.5" median, then first numeric key)
    const timestamps: Date[] = [];
    const values: number[] = [];
    const sortedKeys = Object.keys(modelData).sort();

    for (const ts of sortedKeys) {
      const point = modelData[ts];
      const value = forecastPointCentreValue(point);
      const parsedDate = new Date(ts);
      if (!isNaN(parsedDate.getTime()) && typeof value === "number") {
        timestamps.push(parsedDate);
        values.push(value);
      }
    }

    if (timestamps.length === 0) throw new Error("No valid data points in forecast");

    return {
      timestamps,
      values,
      label: output.name || "Model Output",
      unit: output.unit || models[modelKey]?.yUnit,
    };
  } catch (err) {
    if (err instanceof Error && err.message.includes("No ")) throw err;
    throw new Error("Failed to download forecast data from S3");
  }
}

/** Resolve a dataset's CSV text from S3. Uses the selected record's s3Path if available, otherwise fetches the latest record. Note: files with .gz extension in this system are actually plain CSV (not gzip compressed). */
export async function fetchDatasetCsv(config: DataSourceFormState): Promise<string> {
  if (!config.datasetId) throw new Error("No dataset selected");

  let s3Path = config.datasetRecordS3Path;

  if (!s3Path) {
    const { data } = await client.graphql({
      query: /* GraphQL */ `
        query GetDatasetWithLatestRecord($datasetId: ID!, $limit: Int) {
          dataSetRecordsByDatasetIdAndTimeValue(
            datasetId: $datasetId
            sortDirection: DESC
            limit: $limit
          ) {
            items {
              id
              s3Path
            }
          }
        }
      `,
      variables: { datasetId: config.datasetId, limit: 1 },
    });

    const records = data.dataSetRecordsByDatasetIdAndTimeValue?.items;
    if (!records || records.length === 0) {
      throw new Error("No records found for this dataset");
    }
    s3Path = records[0].s3Path;
  }

  const result = await downloadData({ path: s3Path }).result;
  const blob = await result.body.blob();

  if (s3Path.endsWith(".gz")) {
    const { decompressCSV } = await import("./compressionUtils");
    return decompressCSV(blob);
  }
  return blob.text();
}

/** DATASET: Fetch CSV from S3, parse into rows, optionally apply a data operation. - No operation → single-series PreviewData (legacy shape). - Operation applied → multi-series PreviewData via `series`; legacy `timestamps/values` mirror series[0] so callers like fetchCombinedPreviewData that ignore `series` still get a sensible single line. */
async function fetchDatasetPreview(config: DataSourceFormState): Promise<PreviewData> {
  let csvText: string;
  try {
    csvText = await fetchDatasetCsv(config);
  } catch (err: any) {
    console.error("Dataset preview error:", err);
    throw new Error(err?.message || "Failed to download dataset record from S3");
  }

  const rows = parseCsvRows(csvText);
  const label = config.datasetRecordFileName || config.datasetName || "Dataset";

  if (config.operation?.id) {
    const result = applyOperation(config.operation.id, rows, config.operation.args);
    const first = result.series[0];
    return {
      series: result.series,
      timestamps: first?.timestamps ?? [],
      values: first?.values ?? [],
      label,
      unit: result.unit,
      warnings: result.warnings,
    };
  }

  return {
    timestamps: rows.map((r) => r.timestamp),
    values: rows.map((r) => r.value),
    label,
  };
}

/** Fetch the S3 key of the most recent ForecastRecord for a given output parameter. Used by the ComposedForecast form to render the full ProcessedScatterGraph preview. */
export async function fetchLatestForecastS3Key(outputParamId: string): Promise<string> {
  if (!outputParamId) throw new Error("No output parameter selected");

  const { data } = await client.graphql({
    query: /* GraphQL */ `
      query GetLatestForecastKey($id: ID!) {
        getProcessedModelOutputParam(id: $id) {
          forecasts(sortDirection: DESC, limit: 1) {
            items {
              s3Key
            }
          }
        }
      }
    `,
    variables: { id: outputParamId },
  });

  const s3Key = data.getProcessedModelOutputParam?.forecasts?.items?.[0]?.s3Key;
  if (!s3Key) throw new Error("No forecast records available for this output parameter");
  return s3Key;
}

/** A saved model-output source's chosen variable (metadata.variation); undefined = the output's first. */
export const sourceVariation = (ds: any): string | undefined => parseMetadata(ds?.metadata)?.variation || undefined;

// One variable's { timestamp: { mean, "0.1", … } } map from a processed-model output's latest forecast record:
// the chosen one, else the first
async function fetchLatestProcessedModelData(outputParamId: string, variation?: string): Promise<Record<string, any> | null> {
  const { parsedData } = await fetchAndParseForecastRecord(await fetchLatestForecastS3Key(outputParamId));
  const models = parsedData?.models || {};
  const key = pickModelKey(models, variation);
  return key ? models[key]?.data || null : null;
}

// A Custom View's model forecast draws in the brand olive, the colour its shaded range already uses
// Hex, so a colour input and the quick picks (which include it) read it the same way the chart does
export const customViewModelColor = (): string => cssColorToHex(readSwatch("text-eai-cta-primary"));

/** Each source's colour by id: a colour saved in its display settings wins (the base's mean channel, else an overlay's line colour); otherwise the base model (by role) is the brand olive and the rest take the next Custom View colour. */
export const sourceColors = (unordered: any[]): Record<string, string> => {
  const palette = CUSTOM_VIEW_SOURCE_COLORS;
  const dataSources = orderedSources(unordered);
  const { baseIndex } = resolveSourceRoles(dataSources);
  const colors: Record<string, string> = {};
  let next = 0;
  (dataSources || []).forEach((ds, i) => {
    const display = parseMetadata(ds.metadata).display;
    // A chosen colour doesn't use up a palette slot, so the other sources keep their usual colours
    if (i === baseIndex) colors[ds.id] = display?.channels?.mean?.color || customViewModelColor();
    else colors[ds.id] = display?.color || palette[next++ % palette.length];
  });
  return colors;
};

export interface CombinedPreviewData {
  // First PROCESSED_MODEL_OUTPUT — rendered with full percentile bands
  baseModel: {
    s3Key: string;
    sourceName: string;
    outputId: string;
    /** The variable of a multi-variable output to draw; unset = the first. */
    variation?: string;
    display?: SourceDisplaySettings | null;
    /** The source's multiplier (1 = unchanged); the chart scales the bands and mean by it. */
    weight?: number;
    /** Its place in the source list: 0 is drawn on top. */
    layer?: number;
  } | null;
  // All other sources — rendered as overlay lines on the ProcessedScatterGraph
  overlayLines: Array<{
    name: string;
    /** The data source this line came from, which fixes its colour. */
    sourceId: string;
    /** The displayed source type (DATASET, not S3_DATA). */
    sourceType: string;
    records: Array<{ timestamp: string | Date; value: number }>;
    color: string;
    /** Set on the source scored against the base model (see findAccuracySource). */
    isObservation?: boolean;
    /** A range source (historical output or curated dataset): the chart re-reads it for the base issue's window. */
    rangeRef?: SeriesRef;
    /** The source's unit from the query answer, when it has one. */
    unit?: string;
    /** "daily averages" when the server downsampled this line; null when the rows came back as they are. */
    aggregationNote?: string | null;
    /** The source's multiplier, already applied to `records`; kept so a re-read applies it again. */
    weight?: number;
    /** Its source's place in the list: 0 is drawn on top. */
    layer?: number;
    /** A Postgres parameter: the chart re-reads it for the forecast's window. */
    parameterId?: number;
  }>;
  /** The observation source and its scoring settings, when one is marked. */
  accuracy: AccuracySource | null;
  /** Days of history shown before the forecast starts; null = as long as the forecast. */
  historyDays?: number | null;
  /** What the history ends at: the selected forecast issue (default) or today. */
  historyAnchor?: HistoryAnchor | null;
}

/** Fetch all data sources for a ComposedForecast and format them for a single combined chart. The base model (see resolveSourceRoles) gets the full percentile bands; everything else becomes overlay lines via the additionalDataSources prop. */
export async function fetchCombinedPreviewData(
  unordered: any[]
): Promise<CombinedPreviewData> {
  const dataSources = orderedSources(unordered);
  const { baseIndex } = resolveSourceRoles(dataSources, savedObservationFlag);
  const historyDays = baseIndex >= 0 ? viewHistoryDays(dataSources[baseIndex]) : null;
  const historyAnchor: HistoryAnchor = baseIndex >= 0 && parseMetadata(dataSources[baseIndex].metadata).historyAnchor === "today" ? "today" : "issue";
  const overlayPromises: Array<Promise<CombinedPreviewData["overlayLines"][number] | { __isBaseModel: true; s3Key: string; sourceName: string } | null>> = [];

  for (let index = 0; index < dataSources.length; index++) {
    const ds = dataSources[index];
    // A historical model's series or a curated dataset: one column over a range. No base window is known yet, so
    // the server's default (the newest data) is read here; the chart re-reads for the base issue's window.
    const rangeRef = index === baseIndex ? null : rangeSourceRef(ds);
    if (rangeRef) {
      overlayPromises.push(
        (async () => {
          try {
            // With a base forecast this read is replaced by the chart's windowed re-read, so the lookback (a second
            // call when it isn't 30) only matters for a view without one
            const line = await fetchSeriesWindow(rangeRef, null, {
              lookbackDays: baseIndex >= 0 ? Math.max(DEFAULT_RANGE_LOOKBACK_DAYS, historyDays ?? 0) : rangeSourceLookbackDays(ds),
            });
            return {
              name: ds.sourceName,
              sourceId: ds.id,
              sourceType: displaySourceType(ds),
              records: line.timestamps.map((t, i) => ({ timestamp: t, value: line.values[i] })),
              color: "",
              rangeRef,
              unit: line.unit,
              aggregationNote: shortAggregationNote(line.aggregation),
            };
          } catch {
            return null;
          }
        })()
      );
      continue;
    }
    if (index === baseIndex) {
      // First processed model → base chart with percentile bands We resolve the s3Key here; the component will handle the full render
      overlayPromises.push(
        (async () => {
          try {
            const s3Key = await fetchLatestForecastS3Key(ds.processedModelOutputId);
            return {
              __isBaseModel: true as const,
              s3Key,
              sourceName: ds.sourceName,
              outputId: ds.processedModelOutputId,
              variation: sourceVariation(ds),
              display: parseMetadata(ds.metadata).display ?? null,
              weight: normalizeWeight(ds.weight),
            };
          } catch {
            // If it fails, skip — don't block the whole preview
            return null;
          }
        })()
      );
    } else if (ds.sourceType === "PROCESSED_MODEL_OUTPUT") {
      // Additional processed models → extract mean as overlay line
      overlayPromises.push(
        (async () => {
          try {
            const modelData = await fetchLatestProcessedModelData(ds.processedModelOutputId, sourceVariation(ds));
            if (!modelData) return null;

            // Same extraction as the card's series, so the modal and the card never disagree about a model
            const records = processedModelPoints(modelData).map((p) => ({ timestamp: new Date(p.t).toISOString(), value: p.value }));

            return { name: ds.sourceName, sourceId: ds.id, sourceType: "PROCESSED_MODEL_OUTPUT", records, color: "" };
          } catch {
            return null;
          }
        })()
      );
    } else if (ds.sourceType === "POSTGRES_HISTORICAL") {
      overlayPromises.push(
        (async () => {
          try {
            const meta = parseMetadata(ds.metadata);
            if (!meta.parameterId) return null;
            // Enough rows for the view's history before the forecast, when that asks for more
            const rawRecords = await fetchParameterRecords(
              meta.parameterId,
              Math.max(meta.lookbackDays || 30, historyDays ?? 0)
            );
            if (!rawRecords?.length) return null;
            const records = rawRecords
              .map((r: any) => ({ timestamp: r.timestamp, value: parseFloat(r.value) }))
              .filter((r: any) => !isNaN(r.value))
              .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            return { name: ds.sourceName, sourceId: ds.id, sourceType: "POSTGRES_HISTORICAL", records, color: "", parameterId: meta.parameterId };
          } catch {
            return null;
          }
        })()
      );
    } else if (ds.sourceType === "S3_DATA" && ds.metadata) {
      // S3_DATA with datasetId in metadata = DATASET source (enum workaround)
      const meta = parseMetadata(ds.metadata);
      if (meta.datasetId) {
        const operation = parseOperationFromDataOperations(ds.dataOperations);
        overlayPromises.push(
          (async () => {
            try {
              const previewData = await fetchDatasetPreview({
                sourceType: "DATASET",
                sourceName: ds.sourceName,
                datasetId: meta.datasetId,
                datasetName: meta.datasetName,
                datasetRecordId: meta.datasetRecordId,
                datasetRecordS3Path: meta.datasetRecordS3Path,
                datasetRecordFileName: meta.datasetRecordFileName,
                weight: ds.weight,
                priority: ds.priority,
                operation,
              } as any);

              // Op applied → one overlay line per series
              if (previewData.series && previewData.series.length > 0) {
                return previewData.series.map((s) => ({
                  name: `${ds.sourceName} — ${s.label}`,
                  sourceId: ds.id,
                  sourceType: "DATASET",
                  records: s.timestamps.map((ts, j) => ({ timestamp: ts, value: s.values[j] })),
                  color: "",
                }));
              }

              const records = previewData.timestamps.map((ts, i) => ({
                timestamp: ts,
                value: previewData.values[i],
              }));
              return { name: ds.sourceName, sourceId: ds.id, sourceType: "DATASET", records, color: "" };
            } catch {
              return null;
            }
          })()
        );
      }
    }
  }

  const results = await Promise.all(overlayPromises);

  // Extract the base model and flatten overlay results (some entries may be arrays of multiple series from a single source with a data operation).
  let baseModel: CombinedPreviewData["baseModel"] = null;
  const overlayLines: CombinedPreviewData["overlayLines"] = [];
  for (const r of results) {
    if (!r) continue;
    if (Array.isArray(r)) {
      overlayLines.push(...r);
    } else if ("__isBaseModel" in r) {
      baseModel = { s3Key: r.s3Key, sourceName: r.sourceName, outputId: r.outputId, variation: r.variation, display: r.display, weight: r.weight, layer: baseIndex };
    } else {
      overlayLines.push(r);
    }
  }

  // Each source's weight multiplies its values (a unit bridge — kcfs → cfs = 1000), applied once here for every source type
  const weightById = new Map(dataSources.map((ds) => [ds.id, normalizeWeight(ds.weight)]));
  const layerById = new Map(dataSources.map((ds, i) => [ds.id, i]));
  overlayLines.forEach((line) => {
    line.layer = layerById.get(line.sourceId);
    line.weight = weightById.get(line.sourceId) ?? 1;
    line.records = weightRecords(line.records, line.weight);
  });

  // Each source keeps the colour sourceColors gives it, so the chart, card and detail list agree; a dataset operation's extra series take palette colours past the sources'
  const colors = sourceColors(dataSources);
  const palette = CUSTOM_VIEW_SOURCE_COLORS;
  const seen = new Set<string>();
  let extra = Object.keys(colors).length;
  overlayLines.forEach((line) => {
    if (!seen.has(line.sourceId) && colors[line.sourceId]) line.color = colors[line.sourceId];
    else line.color = palette[extra++ % palette.length];
    seen.add(line.sourceId);
  });

  const accuracy = findAccuracySource(dataSources);
  if (accuracy) {
    for (const line of overlayLines) {
      if (line.name === accuracy.sourceName || line.name.startsWith(`${accuracy.sourceName} — `)) line.isObservation = true;
    }
  }

  return { baseModel, overlayLines, accuracy, historyDays, historyAnchor };
}

export type HistoryAnchor = "issue" | "today";

/**
 * The window a view with a forecast shows: `historyDays` of history (unset = as long as the forecast) ending at the
 * anchor, through the forecast's end. Anchored to the selected issue (the default), stepping to an older issue
 * brings its own history; anchored to today, the history stays the days before now whatever issue is picked.
 */
export function historyWindow(
  forecast: SeriesWindow,
  { historyDays, anchor = "issue", issuedAt, now = Date.now() }: { historyDays?: number | null; anchor?: HistoryAnchor | null; issuedAt?: number | null; now?: number }
): SeriesWindow {
  const span = historyDays && historyDays > 0 ? historyDays * DAY_MS : Math.max(forecast.end - forecast.start, DAY_MS);
  if (anchor === "today") return { start: now - span, end: Math.max(forecast.end, now) };
  // The issue time when it is known (it can sit before the file's first row), else the forecast's first row
  const at = Number.isFinite(issuedAt) ? Math.min(issuedAt as number, forecast.start) : forecast.start;
  return { start: at - span, end: forecast.end };
}

/**
 * The range lines read again for the window the chart settled on: the base issue's span once it is known
 * (decision 2 A). Other lines come back as they were; a failed re-read keeps the line it had.
 */
export async function refetchRangeLines(
  overlayLines: CombinedPreviewData["overlayLines"],
  window: SeriesWindow | null
): Promise<CombinedPreviewData["overlayLines"]> {
  if (!window) return overlayLines;
  return Promise.all(
    overlayLines.map(async (line) => {
      // A Postgres parameter is read for the window too, so an older forecast issue still gets its history
      if (line.parameterId) {
        try {
          // The by-time endpoint returns at most 4,000 rows, newest first (≈41 days of 15-minute data), so a long
          // window is read in chunks that each fit under it; nothing past now exists, so the reads stop there
          const chunks = timeChunks(window.start, Math.min(window.end, Date.now()));
          const pages = await Promise.all(
            chunks.map((c) => fetchParameterRecordsByTime(line.parameterId as number, new Date(c.start), new Date(c.end)))
          );
          return { ...line, records: weightRecords(mergeParameterRows(pages), line.weight) };
        } catch (err) {
          console.error(`Failed to re-read ${line.name} for the forecast's window:`, err);
          return line;
        }
      }
      if (!line.rangeRef) return line;
      try {
        const fetched = await fetchSeriesWindow(line.rangeRef, window);
        return {
          ...line,
          records: weightRecords(fetched.timestamps.map((t, i) => ({ timestamp: t, value: fetched.values[i] })), line.weight),
          unit: fetched.unit ?? line.unit,
          aggregationNote: shortAggregationNote(fetched.aggregation),
        };
      } catch (err) {
        console.error(`Failed to re-read ${line.name} for the forecast's window:`, err);
        return line;
      }
    })
  );
}

// 10 days keeps a chunk under the endpoint's 4,000-row cap down to 5-minute data (2,880 rows); 40 chunks = 400 days
const PARAMETER_CHUNK_MS = 10 * DAY_MS;
const MAX_PARAMETER_CHUNKS = 40;

/** [start, end] split into consecutive pieces of at most `chunkMs`; the newest `max` pieces when it is longer. */
export function timeChunks(
  start: number,
  end: number,
  chunkMs = PARAMETER_CHUNK_MS,
  max = MAX_PARAMETER_CHUNKS
): Array<{ start: number; end: number }> {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  const chunks: Array<{ start: number; end: number }> = [];
  for (let to = end; to > start && chunks.length < max; to -= chunkMs) {
    chunks.unshift({ start: Math.max(start, to - chunkMs), end: to });
  }
  return chunks;
}

/** Chunked parameter rows as one ascending series; a row on a chunk boundary (read by both) is kept once. */
export function mergeParameterRows(pages: unknown[]): Array<{ timestamp: string; value: number }> {
  const byTime = new Map<number, { timestamp: string; value: number }>();
  for (const rows of pages) {
    for (const r of Array.isArray(rows) ? rows : []) {
      const value = parseFloat((r as any)?.value);
      const t = new Date((r as any)?.timestamp).getTime();
      if (!isNaN(value) && Number.isFinite(t)) byTime.set(t, { timestamp: (r as any).timestamp, value });
    }
  }
  return Array.from(byTime.entries())
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row);
}

// ============================================ Forecast accuracy ============================================

export interface ForecastIssue {
  id: string;
  forecastIssued: string;
  s3Key: string;
  /** First / last row time of the issue's file (set by s3Triggerfunc since seriesMetaData; null on older records). */
  seriesStart?: string | null;
  seriesEnd?: string | null;
}

/** Every forecast issue of an output, newest first (ForecastRecord byParameter index, sorted by forecastIssued). */
export async function listForecastIssues(outputParamId: string, maxPages = 20): Promise<ForecastIssue[]> {
  const issues: ForecastIssue[] = [];
  let nextToken: string | null = null;
  for (let page = 0; page < maxPages; page++) {
    const { data } = await client.graphql({
      query: /* GraphQL */ `
        query ListForecastIssues($id: ID!, $nextToken: String) {
          getProcessedModelOutputParam(id: $id) {
            forecasts(sortDirection: DESC, limit: 1000, nextToken: $nextToken) {
              items { id forecastIssued s3Key seriesStart seriesEnd }
              nextToken
            }
          }
        }
      `,
      variables: { id: outputParamId, nextToken },
    });
    const conn = data.getProcessedModelOutputParam?.forecasts;
    issues.push(...(conn?.items ?? []).filter((i: ForecastIssue | null) => i?.s3Key));
    nextToken = conn?.nextToken ?? null;
    if (!nextToken) break;
  }
  return issues;
}

/** The observation source a composed forecast scores its base model against. Stored on the source as
 *  `metadata.accuracy` so it needs no schema change: `{ enabled: true, ...AccuracyConfig }`. */
export interface AccuracySource {
  dataSource: any;
  sourceName: string;
  config: AccuracyConfig;
  /** Colour of the error line (the observation source's display.errorColor). */
  errorColor?: string;
}

/** Form rows with the observation flag dropped where it can't apply: on the only model output (#908). */
export function withoutBaseObservation<T extends { sourceType?: string; accuracy?: unknown }>(dataSources: T[]): T[] {
  const { observationIndex } = resolveFormSourceRoles(dataSources);
  return dataSources.map((ds, i) =>
    (ds as any).accuracy?.enabled && i !== observationIndex ? { ...ds, accuracy: null } : ds
  );
}

export function findAccuracySource(dataSources: any[]): AccuracySource | null {
  const { observationIndex } = resolveSourceRoles(dataSources);
  if (observationIndex < 0) return null;
  const ds = dataSources[observationIndex];
  const { enabled, ...config } = parseMetadata(ds.metadata).accuracy;
  return {
    dataSource: ds,
    sourceName: ds.sourceName,
    config: config as AccuracyConfig,
    errorColor: parseMetadata(ds.metadata).display?.errorColor,
  };
}

/** A forecast model's `data` ({ts: {series: value}}) as scoring points. */
export const forecastPointsFromModelData = (modelData: Record<string, Record<string, unknown>>): ForecastPoint[] =>
  Object.entries(modelData || {})
    .map(([ts, series]) => {
      const date = new Date(ts);
      const t = Number.isNaN(date.valueOf()) ? new Date(`${ts}Z`).getTime() : date.getTime();
      return { t, series: series && typeof series === "object" ? series : { value: series } };
    })
    .filter((p) => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);

/** Observation points; from another model they also say which of its issues was used — or that none covers the window. */
export type ObservationWindow = ObservationPoint[] & { observationIssue?: string; noObservationIssue?: boolean };

/** Observation records inside [start, end] for any supported source: Postgres by time, a dataset, or another model. */
export async function fetchObservationWindow(ds: any, start: Date, end: Date): Promise<ObservationWindow> {
  const meta = parseMetadata(ds.metadata);
  const weight = normalizeWeight(ds.weight);
  const inWindow = (p: ObservationPoint) => p.t >= start.getTime() && p.t <= end.getTime();
  // Observations are scored in the units the chart draws them in: the source's weight applied
  const toPoints = (rows: Array<{ timestamp: any; value: any }>) =>
    rows
      .map((r) => ({ t: new Date(r.timestamp).getTime(), value: Number(r.value) * weight }))
      .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.value) && inWindow(p))
      .sort((a, b) => a.t - b.t);

  if (ds.sourceType === "POSTGRES_HISTORICAL" && meta.parameterId) {
    const rows = await fetchParameterRecordsByTime(meta.parameterId, start, end);
    return toPoints(Array.isArray(rows) ? rows : []);
  }
  // A historical model's series or a curated dataset as the observation: the window's rows from the range query
  const rangeRef = rangeSourceRef(ds);
  if (rangeRef) {
    const line = await fetchSeriesWindow(rangeRef, { start: start.getTime(), end: end.getTime() }, { maxPoints: 10_000 });
    return toPoints(line.timestamps.map((t, i) => ({ timestamp: t, value: line.values[i] })));
  }
  if (meta.datasetId) {
    const preview = await fetchDatasetPreview({
      sourceType: "DATASET",
      sourceName: ds.sourceName,
      datasetId: meta.datasetId,
      datasetName: meta.datasetName,
      datasetRecordId: meta.datasetRecordId,
      datasetRecordS3Path: meta.datasetRecordS3Path,
      datasetRecordFileName: meta.datasetRecordFileName,
      weight: ds.weight,
      priority: ds.priority,
      operation: parseOperationFromDataOperations(ds.dataOperations),
    } as any);
    return toPoints(preview.timestamps.map((t, i) => ({ timestamp: t, value: preview.values[i] })));
  }
  if (ds.sourceType === "PROCESSED_MODEL_OUTPUT" && ds.processedModelOutputId) {
    // Another model as the observation: use ITS issue that covers this window (#909), not its latest run
    const issue = pickObservationIssue(await cachedForecastIssues(ds.processedModelOutputId), start, end);
    if (!issue) return Object.assign([] as ObservationPoint[], { noObservationIssue: true });
    const result = await downloadData({ path: issue.s3Key }).result;
    const parsed = parseForecastFileText(await result.body.text(), issue.s3Key);
    const model = Object.values<any>(parsed?.models || {})[0];
    const points = toPoints(
      forecastPointsFromModelData(model?.data).map((p) => ({ timestamp: p.t, value: forecastPointCentreValue(p.series) }))
    );
    return Object.assign(points, { observationIssue: issue.forecastIssued });
  }
  throw new Error(`Observations are not supported for source type ${ds.sourceType}`);
}

/**
 * The observation model's issue to score a window against: the one whose rows overlap [start, end] the most,
 * newest on a tie. Issues recorded before s3Triggerfunc stored seriesStart/End have no span; when none of the
 * overlapping candidates are known, fall back to the newest issue issued before the window ends.
 */
export function pickObservationIssue(issues: ForecastIssue[], start: Date, end: Date): ForecastIssue | null {
  const s = start.getTime();
  const e = end.getTime();
  let best: { issue: ForecastIssue; overlap: number } | null = null;
  for (const issue of issues) {
    if (!issue.seriesStart || !issue.seriesEnd) continue;
    const overlap = Math.min(e, new Date(issue.seriesEnd).getTime()) - Math.max(s, new Date(issue.seriesStart).getTime());
    if (overlap <= 0) continue;
    const newer = !best || new Date(issue.forecastIssued) > new Date(best.issue.forecastIssued);
    if (!best || overlap > best.overlap || (overlap === best.overlap && newer)) best = { issue, overlap };
  }
  if (best) return best.issue;
  return (
    issues
      .filter((i) => !i.seriesStart && new Date(i.forecastIssued).getTime() <= e)
      .sort((a, b) => new Date(b.forecastIssued).getTime() - new Date(a.forecastIssued).getTime())[0] ?? null
  );
}

// Observation models are looked up once per issue the user steps to; their issue list rarely changes mid-session
const ISSUE_CACHE_MS = 5 * 60_000;
const issueCache = new Map<string, { at: number; issues: Promise<ForecastIssue[]> }>();
const cachedForecastIssues = (outputParamId: string): Promise<ForecastIssue[]> => {
  const hit = issueCache.get(outputParamId);
  if (hit && Date.now() - hit.at < ISSUE_CACHE_MS) return hit.issues;
  const issues = listForecastIssues(outputParamId).catch((err) => {
    issueCache.delete(outputParamId);
    throw err;
  });
  issueCache.set(outputParamId, { at: Date.now(), issues });
  return issues;
};

export interface IssueAccuracy {
  observations: ObservationPoint[];
  result: AccuracyResult;
  /** When the observation is another model: the issue of it that was used (shown in the summary). */
  observationIssue?: string;
}

/** Score one forecast issue: fetch the observation over the issue's own window, then compare. */
export async function loadIssueAccuracy(
  accuracy: AccuracySource,
  modelData: Record<string, Record<string, unknown>>
): Promise<IssueAccuracy> {
  const points = forecastPointsFromModelData(modelData);
  if (!points.length) {
    return { observations: [], result: { matched: 0, summary: {}, series: [], warnings: ["The forecast has no data."] } };
  }
  const start = new Date(points[0].t - 3_600_000);
  const end = new Date(points[points.length - 1].t + 3_600_000);
  const observations = await fetchObservationWindow(accuracy.dataSource, start, end);
  const result = scoreForecast(points, observations, accuracy.config);
  if (observations.noObservationIssue) {
    // More useful than the generic "no observations overlap": the observation model has no issue for this period
    result.warnings = [`No issue of ${accuracy.sourceName} covers this forecast's window.`];
  }
  return {
    observations,
    result,
    observationIssue: observations.observationIssue,
  };
}

/**
 * The y-axis title for a view drawn without a forecast: the lines' shared unit (from the query answer, else a
 * trailing "(unit)" in their names, e.g. "Big Beaver Creek hourly (ft³/s)"); "Value" when they don't share one.
 */
export function overlayAxisUnit(lines: Array<{ name?: string; unit?: string }>): string {
  // A bracketed name suffix only counts when it looks like a unit (ft³/s, °C, MW), not a note like "(observed)"
  const looksLikeUnit = (text: string) => /[\/³²°%]/.test(text) || /^[A-Za-zµ]{1,4}$/.test(text);
  const unitOf = (line: { name?: string; unit?: string }) => {
    if (line.unit?.trim()) return line.unit.trim();
    const suffix = line.name?.match(/\(([^()]+)\)\s*$/)?.[1]?.trim();
    return suffix && looksLikeUnit(suffix) ? suffix : null;
  };
  const units = new Set(lines.map(unitOf).filter(Boolean));
  return units.size === 1 ? (Array.from(units)[0] as string) : "Value";
}

export interface PreviewWindowFit {
  overlayLines: CombinedPreviewData["overlayLines"];
  /** Lines with no point in the window, with their last timestamp, so the chart can say why they're missing. */
  outside: Array<{ sourceId: string; name: string; latest: number }>;
}

const MIN_WINDOW_MS = DAY_MS;

/** First and last of a list of times, by loop rather than Math.min(...times), which throws once a long series passes the engine's argument limit. */
export function timeSpan(times: number[]): { start: number; end: number } | null {
  let start = Infinity;
  let end = -Infinity;
  for (const t of times) {
    if (t < start) start = t;
    if (t > end) end = t;
  }
  return times.length ? { start, end } : null;
}

/** Clips overlay lines to one window so a stale source can't stretch the axis over years: the base model's span plus as much history again before it, else the freshest line's span the same way. */
export function fitPreviewWindow(
  overlayLines: CombinedPreviewData["overlayLines"],
  anchor: { start: number; end: number } | null,
  /** The forecast view's window (historyWindow); unset = as much history before the anchor as it is long. */
  window?: SeriesWindow | null
): PreviewWindowFit {
  const times = (line) => line.records.map((r) => new Date(r.timestamp).getTime()).filter(Number.isFinite);
  let range = anchor;
  if (!range) {
    // No model: the line that ends last sets the window
    range = overlayLines.reduce((best, line) => {
      const span = timeSpan(times(line));
      if (!span) return best;
      return !best || span.end > best.end ? span : best;
    }, null as { start: number; end: number } | null);
  }
  if (!range) return { overlayLines, outside: [] };

  const span = Math.max(range.end - range.start, MIN_WINDOW_MS);
  const from = anchor && window ? window.start : range.start - span;
  const to = anchor && window ? window.end : range.end;
  const fitted: CombinedPreviewData["overlayLines"] = [];
  const outside: PreviewWindowFit["outside"] = [];
  overlayLines.forEach((line) => {
    const records = line.records.filter((r) => {
      const t = new Date(r.timestamp).getTime();
      return t >= from && t <= to;
    });
    if (records.length) fitted.push({ ...line, records });
    else {
      const lineSpan = timeSpan(times(line));
      if (lineSpan) outside.push({ sourceId: line.sourceId, name: line.name, latest: lineSpan.end });
    }
  });
  return { overlayLines: fitted, outside };
}

export interface PreviewLegendEntry {
  id: string;
  name: string;
  color: string;
  note: string;
  /** Faded: it has no line in the chart, or is switched off. */
  dim: boolean;
  /** "daily averages" when the drawn line was downsampled, so the legend can say so without a hover. */
  averaged?: string | null;
}

const shortDate = (t: number) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** One legend entry per saved source, so none goes missing: its type when drawn, else why not (out of the window, empty, or failed to load), flagged when inactive. */
export function previewLegend(
  unordered: any[],
  {
    loadedLines,
    fit,
    baseModelDrawn,
    baseModelBanded,
  }: { loadedLines: CombinedPreviewData["overlayLines"]; fit: PreviewWindowFit; baseModelDrawn: boolean; baseModelBanded: boolean }
): PreviewLegendEntry[] {
  const sources = orderedSources(unordered);
  const colors = sourceColors(sources);
  const drawn = new Set(fit.overlayLines.map((line) => line.sourceId));
  const loaded = new Set(loadedLines.map((line) => line.sourceId));
  const latest = new Map<string, number>();
  fit.outside.forEach((o) => latest.set(o.sourceId, Math.max(o.latest, latest.get(o.sourceId) ?? 0)));
  // The base model leads, as on the card; the rest keep their saved order
  // By role, as the chart picks it — an observation model listed first isn't the base (#908)
  const { baseIndex } = resolveSourceRoles(sources);
  const baseId = sources?.[baseIndex]?.id;
  const ordered = baseIndex > 0 ? [sources[baseIndex], ...sources.filter((_, i) => i !== baseIndex)] : sources || [];
  return ordered.map((ds) => {
    const isBase = baseIndex >= 0 && ds.id === baseId;
    const type = formatSourceType(displaySourceType(ds));
    let note: string;
    let dim = false;
    let averaged: string | null = null;
    const aggregationNote = fit.overlayLines.find((line) => line.sourceId === ds.id)?.aggregationNote;
    if (isBase ? baseModelDrawn : drawn.has(ds.id)) {
      note = isBase && baseModelBanded ? `${type} · shaded range` : aggregationNote ? `${type} · ${aggregationNote}` : type;
      if (!(isBase && baseModelBanded)) averaged = aggregationNote ?? null;
    } else if (latest.has(ds.id)) [note, dim] = [`No data here · latest ${shortDate(latest.get(ds.id))}`, true];
    else if (loaded.has(ds.id)) [note, dim] = ["No data yet", true];
    else [note, dim] = ["Couldn't load", true];
    if (ds.isActive === false) [note, dim] = [`Inactive · ${note}`, true];
    return { id: ds.id, name: ds.sourceName || "Untitled source", color: colors[ds.id], note, dim, averaged: dim ? null : averaged };
  });
}

// ============================================ Custom View Series ============================================

// Line value from forecastPointCentreValue (mean, else median, else outer-band midpoint, else first number); outermost quantiles become the band
const processedModelPoints = (modelData: Record<string, any>): SparklinePoint[] =>
  Object.keys(modelData)
    .map((ts) => {
      const row = modelData[ts] || {};
      const quantiles = Object.keys(row)
        .filter((k) => !Number.isNaN(Number(k)))
        .map((k) => row[k])
        .filter((v) => typeof v === "number");
      const hasBand = quantiles.length > 1;
      return {
        t: new Date(ts).getTime(),
        value: forecastPointCentreValue(row),
        low: hasBand ? Math.min(...quantiles) : undefined,
        high: hasBand ? Math.max(...quantiles) : undefined,
      };
    })
    .filter((p) => typeof p.value === "number" && !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);

// How many sources a card reads out; the rest are counted, not fetched
export const CARD_SOURCE_LIMIT = 3;

export interface SourceSeries {
  id: string;
  name: string;
  /** The source's displayed type — DATASET for a dataset saved as S3_DATA. */
  sourceType: string;
  points: SparklinePoint[];
  /** The source's unit when it has one — model outputs do, datasets and historical feeds don't. */
  unit?: string;
  /** Same colour the combined chart draws this source in. */
  color: string;
}

export interface ForecastSeries {
  /** The card's sources in reading order, each with whatever points it returned (empty when it has none yet). */
  sources: SourceSeries[];
}

// The base model (by role) leads, then other model outputs, then everything else in the order it was added — the order a card reads its sources out in
export const cardSources = (forecast: any): any[] => {
  const isModel = (ds: any) => ds.sourceType === "PROCESSED_MODEL_OUTPUT";
  const all = dashboardSources(forecast);
  const base = all[resolveSourceRoles(all).baseIndex];
  const rest = all.filter((ds) => ds !== base);
  return [...(base ? [base] : []), ...rest.filter(isModel), ...rest.filter((ds) => !isModel(ds))].slice(0, CARD_SOURCE_LIMIT);
};

// One source's points: a model's forecast (with its band), else the first line its preview returns
async function fetchSourcePoints(ds: any): Promise<SparklinePoint[]> {
  try {
    if (ds.sourceType === "PROCESSED_MODEL_OUTPUT") {
      if (!ds.processedModelOutputId) return [];
      const modelData = await fetchLatestProcessedModelData(ds.processedModelOutputId, sourceVariation(ds));
      return modelData ? weightPoints(processedModelPoints(modelData), ds.weight) : [];
    }
    const { overlayLines } = await fetchCombinedPreviewData([ds]);
    return overlayLines[0] ? recordsToPoints(overlayLines[0].records) : [];
  } catch {
    // No forecast record yet, or a fetch failed — the row reads as no data
    return [];
  }
}

/** The card's sources (up to CARD_SOURCE_LIMIT), fetched in parallel so each row's reading lands together. */
async function fetchForecastSeries(forecast: any): Promise<ForecastSeries> {
  const colors = sourceColors(dashboardSources(forecast));
  const sources = await Promise.all(
    cardSources(forecast).map(async (ds) => ({
      id: ds.id,
      name: ds.sourceName || formatSourceType(displaySourceType(ds)),
      sourceType: displaySourceType(ds),
      points: await fetchSourcePoints(ds),
      unit: ds.processedModelOutput?.unit || undefined,
      color: colors[ds.id],
    }))
  );
  return { sources };
}

// Identity of what a card reads: each source's own updatedAt too, since the form saves the view before its sources
export const forecastSeriesKey = (forecast: any): string =>
  `${forecast.id}:${forecast.updatedAt || ""}:${(forecast?.dataSources?.items || [])
    .map((ds: any) => `${ds.id}@${ds.updatedAt || ""}`)
    .join(",")}`;

// Quick revisits reuse the series; past this a new model run may have landed, so the next mount refetches
const SERIES_TTL_MS = 10 * 60 * 1000;

// One live key per view, so an edit frees the old series
const seriesCache = new Map<string, Promise<ForecastSeries>>();
const seriesFetchedAt = new Map<string, number>();
const seriesKeyById = new Map<string, string>();

export const getForecastSeries = (forecast: any): Promise<ForecastSeries> => {
  const key = forecastSeriesKey(forecast);
  let pending = seriesCache.get(key);
  if (pending && Date.now() - (seriesFetchedAt.get(key) || 0) > SERIES_TTL_MS) pending = undefined;
  if (!pending) {
    const previous = seriesKeyById.get(forecast.id);
    if (previous && previous !== key) seriesCache.delete(previous);
    seriesKeyById.set(forecast.id, key);
    pending = fetchForecastSeries(forecast);
    seriesFetchedAt.set(key, Date.now());
    // The fetchers swallow errors, so any empty source may be a network blip — never keep a result with one, so the next mount retries
    const drop = () => seriesCache.get(key) === pending && seriesCache.delete(key);
    pending.then((series) => series.sources.some((src) => src.points.length < 2) && drop(), drop);
    seriesCache.set(key, pending);
  }
  return pending;
};

// How far ahead the base model's forecast still reaches from now, in days; null with no model or once its last point has passed
export const forecastHorizonDays = (series: ForecastSeries | null | undefined, now = Date.now()): number | null => {
  // cardSources puts the base model first, so the first model output is the base, not an observation model
  const model = series?.sources.find((src) => src.sourceType === "PROCESSED_MODEL_OUTPUT");
  const last = model?.points[model.points.length - 1]?.t;
  return last && last > now ? (last - now) / (24 * 60 * 60 * 1000) : null;
};

// "9-day horizon", or hours under a day; read from the model's data rather than a stored field, so it always matches the chart
export const describeForecastHorizon = (series: ForecastSeries | null | undefined, now = Date.now()): string | null => {
  const days = forecastHorizonDays(series, now);
  if (days === null) return null;
  return days >= 1 ? `${Math.round(days)}-day horizon` : `${Math.max(1, Math.round(days * 24))}-hour horizon`;
};

// TODO: Allow users to choose chart type (bar, scatter, etc.) in future iteration

// ============================================ Customer-facing Display Helpers ============================================

// Stored values are machine strings; every word a customer reads is worded once, here.

/** A forecast issue time as the Date Issued picker and the accuracy summary show it: "Oct 5, 2026, 06:00 PM". */
export const formatIssueTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.valueOf())
    ? iso
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Sentence-case labels for the four configurable forecast types
export const FORECAST_TYPE_LABELS: Record<string, string> = {
  ensemble: "Ensemble",
  weighted: "Weighted",
  "ml-based": "ML-based",
  statistical: "Statistical",
};

// Falls back to the raw value so an unmapped type still reads, rather than vanishing
export const formatForecastType = (forecastType?: string | null): string =>
  FORECAST_TYPE_LABELS[forecastType || ""] || forecastType || "Unspecified";

// "weighted_average" -> "Weighted average": underscores out, first word capitalised
export const formatAggregationMethod = (method?: string | null): string => {
  if (!method) return "Unspecified";
  const words = method.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// What each source type is called in the UI — the enum names are backend vocabulary
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  POSTGRES_HISTORICAL: "Historical",
  PROCESSED_MODEL_OUTPUT: "Model output",
  DATASET: "Dataset",
  ZARR_CHANNEL: "Zarr channel",
  EXTERNAL_API: "External API",
  S3_DATA: "S3 data",
  GROUND_TRUTH: "Ground truth",
  MANUAL_INPUT: "Manual input",
  STATISTICAL_MODEL: "Statistical model",
};

export const formatSourceType = (sourceType?: string | null): string =>
  SOURCE_TYPE_LABELS[sourceType || ""] || sourceType || "Unknown";

export const dashboardSources = (forecast: any): any[] => orderedSources(forecast?.dataSources?.items);

// A dataset source is saved as S3_DATA with a datasetId (the enum has no DATASET member), so it is labelled by what it is
export const displaySourceType = (ds: any): string =>
  ds?.sourceType === "S3_DATA" && parseMetadata(ds?.metadata).datasetId ? "DATASET" : ds?.sourceType || "";

// lastProcessed is the real signal; updatedAt only says the config was edited, so it is worded apart
export const describeDashboardFreshness = (forecast: any): { label: string; tone: string } => {
  const processed = formatTimeAgo(forecast?.lastProcessed);
  if (processed) return { label: `Ran ${processed}`, tone: "text-eai-ink-subtle" };
  const edited = formatTimeAgo(forecast?.updatedAt);
  if (edited) return { label: `Edited ${edited}`, tone: "text-eai-ink-faint" };
  return { label: "Never run", tone: "text-eai-ink-faint" };
};

export const linkedAssetsOf = (forecast: any): any[] =>
  (forecast?.linkedAssets?.items || []).filter((link: any) => link?.asset);

// "Wind Farm A +2" — one name beats a bare count, and the rest fold into a suffix that fits one line
export const describeLinkedAssets = (forecast: any): string => {
  const names = linkedAssetsOf(forecast).map((link: any) => link.asset.name);
  if (names.length === 0) return "Not linked to an asset";
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
};

// Matches name, type, sources and linked assets; lives here so the page filters without the list
export const filterComposedForecasts = (forecasts: any[], searchValue: string): any[] => {
  const term = (searchValue || "").trim().toLowerCase();
  if (!term) return forecasts;
  return forecasts.filter((forecast) => {
    const haystack = [
      forecast?.name,
      forecast?.description,
      formatForecastType(forecast?.forecastType),
      formatAggregationMethod(forecast?.aggregationMethod),
      ...dashboardSources(forecast).flatMap((ds: any) => [ds?.sourceName, formatSourceType(displaySourceType(ds))]),
      ...linkedAssetsOf(forecast).map((link: any) => link.asset.name),
    ];
    return haystack.some((value) => String(value || "").toLowerCase().includes(term));
  });
};

// Sort mirrors Model Garden's toolbar options so the list pages stay consistent
export const COMPOSED_FORECAST_SORT_OPTIONS = [
  { value: "name", label: "Name A–Z" },
  { value: "recent", label: "Recently updated" },
  { value: "created", label: "Newest" },
  { value: "status", label: "Status" },
];

export const sortComposedForecasts = (forecasts: any[], sortBy: string): any[] => {
  const time = (value?: string | null) => (value ? new Date(value).getTime() : 0);
  const sorted = [...forecasts];
  switch (sortBy) {
    case "recent":
      return sorted.sort(
        (a, b) => time(b.lastProcessed || b.updatedAt) - time(a.lastProcessed || a.updatedAt)
      );
    case "created":
      return sorted.sort((a, b) => time(b.createdAt) - time(a.createdAt));
    case "status":
      return sorted.sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive) || (a.name || "").localeCompare(b.name || "")
      );
    default:
      return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }
};
